import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  showNotif
} from '@p81-common/ui';
import type { AutocompleteOptionProps } from '@p81-common/ui';
import { Heading } from 'components/common/Elements/Elements';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';

interface TenantIdsAutocompleteProps {
  autocompleteOptionsMultiple: AutocompleteOptionProps[];
  setAutocompleteOptionsMultiple: React.Dispatch<
    React.SetStateAction<AutocompleteOptionProps[]>
  >;
}

export const MAX_TENANT_IDS = 250;

const TenantIdsAutocomplete: React.FC<TenantIdsAutocompleteProps> = ({
  autocompleteOptionsMultiple,
  setAutocompleteOptionsMultiple
}) => {
  const classes = useStyles();
  const { t } = useTranslation(['activity']);
  const isOverLimit = autocompleteOptionsMultiple.length > MAX_TENANT_IDS;

  const handleAutocompleteCreate: AutocompleteProps['handleCreate'] =
    useCallback(
      (e, v) => {
        if (e.type === 'blur') return;
        const lastRaw = v[v.length - 1];
        const value = (lastRaw ?? '')
          .toString()
          .trim()
          .replace(/,/g, ' ')
          .replace(/\s+/g, ' ');
        if (!value) return;
        const newValue = value.split(' ');
        const uniqueOptions = new Set(
          [
            ...autocompleteOptionsMultiple.map(option => option.name),
            ...newValue
          ].filter(str => str !== '')
        );
        const allOptions = Array.from(uniqueOptions);
        if (allOptions.length > MAX_TENANT_IDS) {
          showNotif(
            t('activity:featureFlag.CUSTOMERS_LIMIT_MESSAGE', {
              limit: MAX_TENANT_IDS
            }),
            { alertProps: { severity: 'error' } }
          );
        }
        const updatedOptions = allOptions.map(name => ({
          name,
          selected: true
        }));

        setAutocompleteOptionsMultiple(updatedOptions);
      },
      [autocompleteOptionsMultiple, setAutocompleteOptionsMultiple, t]
    );

  const handleAutocompleteDelete: AutocompleteProps['handleDelete'] = (
    e,
    v
  ) => {
    if ((e as React.KeyboardEvent).key === 'Enter') {
      return;
    }
    setAutocompleteOptionsMultiple(v);
  };

  const handleAutocompleteClear: AutocompleteProps['handleClear'] = () => {
    setAutocompleteOptionsMultiple([]);
  };

  const autocompleteValueMultiple = useMemo(
    () => autocompleteOptionsMultiple.filter(o => o.selected),
    [autocompleteOptionsMultiple]
  );

  return (
    <Box className={classes.container}>
      <Heading className={classes.title}>
        {t('activity:featureFlag.LIST_OF_TENANT_IDS')}
      </Heading>
      {isOverLimit && (
        <Box className={classes.errorMessage}>
          {t('activity:featureFlag.CUSTOMERS_LIMIT_MESSAGE', {
            limit: MAX_TENANT_IDS
          })}
        </Box>
      )}
      <Autocomplete
        className={cn(classes.autocomplete, isOverLimit && classes.error)}
        handleClear={handleAutocompleteClear}
        handleCreate={handleAutocompleteCreate}
        handleDelete={handleAutocompleteDelete}
        value={autocompleteValueMultiple}
        options={autocompleteOptionsMultiple}
        size={'medium'}
        multiple={true}
        defaultLimitTags={-1}
        alwaysExpand={true}
        showLimitTagsTooltip={true}
        freeSolo={true}
        placeholder={
          autocompleteOptionsMultiple.length
            ? ''
            : t('activity:featureFlag.AUTOCOMPLETE_PLACEHOLDER_TENANT_IDS')
        }
      />
    </Box>
  );
};

export default TenantIdsAutocomplete;
